import type { ElementRef, TemplateRef } from '@angular/core';
import {
  Component,
  computed,
  forwardRef,
  input,
  model,
  output,
  signal,
  viewChild,
} from '@angular/core';
import type { FormValueControl } from '@angular/forms/signals';
import { TranslatePipe } from '@ngx-translate/core';
import type { PropertyPath } from 'lodash';
import { castArray } from 'lodash';
import type {
  OnyxDropdownConfig,
  OnyxDropdownMaxHeight,
  OnyxDropdownOptions,
  OnyxDropdownOptionsInput,
  OnyxDropdownValue,
  OnyxOption,
} from '../../../directives/interfaces';
import { ONYX_FOCUSABLE } from '../../../directives/onyx-auto-focus.directive';
import { OnyxDropdownDirective } from '../../../directives/onyx-dropdown.directive';
import { OnyxOverflowDirective } from '../../../directives/onyx-overflow.directive';
import { DropdownHelper, UtilityHelper } from '../../../helpers';
import type { OnyxOptional } from '../../../interfaces';
import { OnyxBaseFormControlComponent } from '../../../internal/components/onyx-base-form-control/onyx-base-form-control.component';
import { I18N_NAMESPACE } from '../../../internal/constants';
import { OnyxFilterPipe } from '../../../pipes/onyx-filter.pipe';
import { OnyxClearButtonComponent } from '../../buttons/onyx-clear-button/onyx-clear-button.component';
import { OnyxEmojiComponent } from '../../emoji/onyx-emoji/onyx-emoji.component';
import { OnyxErrorComponent } from '../../error/onyx-error/onyx-error.component';
import { OnyxIconComponent } from '../../icons/onyx-icon/onyx-icon.component';
import type { OnyxIconName } from '../../icons/interfaces';
import type { OnyxLabelLink } from '../../labels/interfaces';
import { OnyxInputLabelComponent } from '../../labels/onyx-input-label/onyx-input-label.component';
import { OnyxNotificationNumberLabelComponent } from '../../labels/onyx-notification-number-label/onyx-notification-number-label.component';
import type { OnyxPaths } from '../../table/interfaces';
import { OnyxTagComponent } from '../../tag/onyx-tag/onyx-tag.component';
import type { OnyxTooltipContext } from '../../tooltip/interfaces';
import { ONYX_DROPDOWN_LIMIT } from '../constants';

const I18N = `${I18N_NAMESPACE}.dropdown`;

@Component({
  selector: 'onyx-dropdown',
  imports: [
    OnyxInputLabelComponent,
    OnyxIconComponent,
    OnyxDropdownDirective,
    OnyxOverflowDirective,
    OnyxClearButtonComponent,
    OnyxTagComponent,
    OnyxErrorComponent,
    TranslatePipe,
    OnyxNotificationNumberLabelComponent,
    OnyxEmojiComponent,
  ],
  providers: [
    {
      provide: OnyxFilterPipe,
      useClass: OnyxFilterPipe,
    },
    {
      provide: ONYX_FOCUSABLE,
      useExisting: forwardRef(() => OnyxDropdownComponent),
    },
  ],
  templateUrl: './onyx-dropdown.component.html',
  styleUrl: './onyx-dropdown.component.scss',
  host: { '[style.width]': `width()` },
})
export class OnyxDropdownComponent<OptionValue, Value>
  extends OnyxBaseFormControlComponent
  implements FormValueControl<OnyxDropdownValue<Value>>
{
  public readonly value = model<OnyxDropdownValue<Value>>(null);
  public readonly label = input<string>();
  public readonly placeholder = input(`${I18N}.choose`);
  public readonly hint = input<OnyxTooltipContext>();
  public readonly link = input<OnyxLabelLink | null>(null);
  public readonly options =
    input<OnyxDropdownOptionsInput<OptionValue, Value>>();
  public readonly templateRef = input<TemplateRef<any>>();
  public readonly compareKey = input<OnyxPaths<OptionValue> | PropertyPath>();
  public readonly query = model('');
  public readonly optional = input(false);
  public readonly multiple = input(false);
  public readonly showSelectAll = input(true);
  public readonly attached = model(false);
  public readonly search = input(false);
  public readonly searchPlaceholder = input<string>();
  public readonly searchKeys =
    input<(OnyxPaths<OnyxOption<OptionValue>> | PropertyPath)[]>();
  public readonly limit = input(ONYX_DROPDOWN_LIMIT);
  public readonly notFoundMessage = input<string>();
  public readonly showAddOption = input<boolean | 'value'>(false);
  public readonly addOptionMessage = input<string>();
  public readonly subheading = input<string>();
  public readonly subheadingTemplateRef = input<TemplateRef<any>>();
  public readonly optionTemplateRef = input<TemplateRef<any>>();
  public readonly size = input<32 | 40>(40);
  public readonly optionSize =
    input<OnyxDropdownConfig<never>['optionSize']>(40);
  public readonly theme = input<OnyxDropdownConfig<never>['theme']>('light');
  public readonly overlayWidth =
    input<OnyxDropdownConfig<never>['width']>('inherit');
  public readonly showValueIcon = input(false);
  public readonly ribbon = input(false);
  public readonly ribbonCount = input<number>();
  public readonly transparentRibbon = input(false);
  public readonly transparentRibbonColoredOutput = input(false);
  public readonly ribbonRightIcon = input<OnyxIconName>();
  public readonly showRibbonValue = input(true);
  public readonly showRequired = input(true);
  public readonly showErrors = input(true);
  public readonly showClear = input(true);
  public readonly width = input('100%');
  public readonly maxHeight = input<OnyxDropdownMaxHeight>('360px');

  public readonly optionsChange = output<OnyxDropdownOptions<OptionValue>>();
  public readonly totalItemsChange = output<number>();
  public readonly selectedValueChange = output<{
    value: Value | null;
    isManual: boolean;
  }>();
  public readonly selectedValuesChange = output<{
    values: Value[];
    isManual: boolean;
  }>();
  public readonly selectedOptionChange = output<{
    option: OnyxOption<OptionValue> | null;
    isManual: boolean;
  }>();
  public readonly selectedOptionsChange = output<{
    options: OnyxOption<OptionValue>[];
    isManual: boolean;
  }>();
  public readonly selectedOptionValueChange = output<{
    value: OptionValue | null;
    isManual: boolean;
  }>();
  public readonly selectedOptionValuesChange = output<{
    values: OptionValue[];
    isManual: boolean;
  }>();
  public readonly addOption = output<string | undefined>();

  protected readonly I18N = I18N;

  protected readonly UtilityHelper = UtilityHelper;
  protected readonly isSource = DropdownHelper.isSource;

  private readonly dropdownElementRef =
    viewChild.required<ElementRef<HTMLInputElement>>('dropdownElement');

  protected page = signal(1);
  protected totalItems = signal<number | null>(null);
  protected fetching = signal(false);

  protected selectedOptions = signal<OnyxOption<OptionValue>[]>([]);
  protected selectedName = computed(
    () => this.selectedOptions().at(0)?.name ?? null,
  );
  protected selectedValue = computed(
    () => castArray(this.value()).at(0) ?? null,
  );

  protected isFetchingData = computed(() =>
    this.isSource(this.options())
      ? this.fetching()
      : !this.disabled() && this.options() === null,
  );
  protected tagColor = computed(() =>
    this.disabled() ? 'light-gray' : 'white',
  );

  public focus(): void {
    if (this.disabled()) return;

    this.dropdownElementRef().nativeElement.focus({ focusVisible: true });
  }

  protected changeValue(value: OnyxOptional<OnyxDropdownValue<Value>>): void {
    value ??= this.multiple() ? [] : null;

    this.value.set(value);
    this.touch.emit();
  }

  protected clearValue(): void {
    this.query.set('');
    this.attached.set(false);

    this.changeValue(null);
    this.selectedValueChange.emit({ value: null, isManual: true });
    this.selectedValuesChange.emit({ values: [], isManual: true });
  }
}
